import ApiService from './api';
import logger from '../utils/logger';

/**
 * DataForSEO Service
 * Integrates with backend /run-maps endpoint which proxies DataForSEO API
 */
class DataForSEOService {
    /**
     * Get keyword suggestions from DataForSEO for India
     * @param {Object} params
     * @param {string} params.keyword - Seed keyword
     * @param {number} params.limit - Number of results (default: 10)
     * @returns {Promise<Object>} Keyword suggestions data for India market
     */
    async getKeywordSuggestions({ keyword, limit = 10, location = null }) {
        try {
            logger.log('[DataForSEO] Fetching keyword suggestions for India:', keyword);

            // India only - location handled by backend
            const payload = { keyword, limit };
            if (location) payload.location = location;
            const response = await ApiService.post('/customer/profile/keyword-suggestions', payload);

            // Backend wraps response in { data: {...} } format
            const data = response?.data || response;

            if (!data || !data.keywords) {
                logger.warn('[DataForSEO] No keyword suggestions returned');
                return { keywords: [], seedKeywordData: null };
            }

            logger.log(`[DataForSEO] Found ${data.keywords.length} keyword suggestions`);
            
            return {
                seedKeyword: data.seedKeyword,
                seedKeywordData: data.seedKeywordData,
                totalCount: data.totalCount,
                keywords: this.transformKeywordSuggestions(data.keywords)
            };

        } catch (error) {
            logger.error('[DataForSEO] Keyword suggestions error:', error);
            throw error;
        }
    }

    /**
     * Transform keyword suggestions to match KeywordsTable format
     * @param {Array} keywords - Raw keyword data from API
     * @returns {Array} Transformed keywords
     */
    transformKeywordSuggestions(keywords) {
        return keywords.map(item => {
            const monthlySearches = item.monthlySearches || [];
            let searchVolumeChange = 0;
            
            if (monthlySearches.length >= 2) {
                const lastMonth = monthlySearches[0]?.search_volume || 0;
                const prevMonth = monthlySearches[1]?.search_volume || 0;
                if (prevMonth > 0) {
                    searchVolumeChange = Math.round(((lastMonth - prevMonth) / prevMonth) * 100);
                }
            }

            const competitionLevel = item.competitionLevel || 'Unknown';
            const competition = competitionLevel === 'HIGH' ? 'High' : 
                               competitionLevel === 'MEDIUM' ? 'Medium' : 
                               competitionLevel === 'LOW' ? 'Low' : 'Medium';

            return {
                searchTerm: item.keyword,
                searchVolume: item.searchVolume || 0,
                searchVolumeChange,
                clientRanking: 'Not ranked',
                competitorRanking: '-',
                competition,
                cpc: item.cpc || 0,
                source: 'dataforseo'
            };
        });
    }

    /**
     * Run a maps search using DataForSEO
     * @param {Object} params
     * @param {string} params.keyword - Search keyword
     * @param {number} params.latitude - Center latitude
     * @param {number} params.longitude - Center longitude
     * @param {number} params.radius - Search radius in meters (default 5000)
     * @returns {Promise<Array>} Array of place results
     */
    async searchMaps({ keyword, latitude, longitude, radius = 5000 }) {
        try {
            logger.log('🔍 [DataForSEO] Starting maps search:', { keyword, latitude, longitude, radius });

            const payload = {
                keyword,
                centerLat: latitude,
                centerLng: longitude,
                searchRadiusMeters: radius,
                gridSize: 1, // Single point search for now
                maxCrawlPages: 1,
                depth: 20
            };

            // Call the backend endpoint using the standard ApiService
            // This handles authentication and base URL automatically
            const response = await ApiService.post('/customer/profile/run-maps', payload);

            if (!response || !response.tasks || response.tasks.length === 0) {
                logger.warn('🔍 [DataForSEO] No tasks returned');
                return [];
            }

            // Extract items from the first task result
            const task = response.tasks[0];
            const result = task.result && task.result.length > 0 ? task.result[0] : null;

            if (!result || !result.items) {
                logger.warn('🔍 [DataForSEO] No items found in task result');
                return [];
            }

            logger.log(`🔍 [DataForSEO] Found ${result.items.length} items`);
            
            // Log first item sample to see structure
            if (result.items.length > 0) {
                logger.log('🔍 [DataForSEO] Sample raw item:', result.items[0]);
            }
            
            const transformed = this.transformResults(result.items);
            
            // Log first transformed item to verify photoCount
            if (transformed.length > 0) {
                logger.log('🔍 [DataForSEO] Sample transformed item:', {
                    name: transformed[0].name,
                    reviewsCount: transformed[0].reviewsCount,
                    photoCount: transformed[0].photoCount,
                    rating: transformed[0].rating
                });
            }
            
            return transformed;

        } catch (error) {
            logger.error('🔍 [DataForSEO] Search error:', error);
            return [];
        }
    }

    /**
     * Transform DataForSEO results to standard format
     * @param {Array} items - Raw DataForSEO items
     * @returns {Array} Transformed results
     */
    transformResults(items) {
        return items.map((item, index) => {
            // Extract photo count from various possible sources
            let photoCount = 0;
            
            // Log the item structure for debugging (first item only)
            if (index === 0) {
                logger.log('🔍 [DataForSEO] Raw item sample:', {
                    title: item.title,
                    rating: item.rating,
                    available_keys: Object.keys(item).slice(0, 20) // Show first 20 keys
                });
            }
            
            // Check if there's a photos_count field in the raw data
            if (typeof item.photos_count === 'number' && item.photos_count > 0) {
                photoCount = item.photos_count;
            } 
            // Check if there's an images array
            else if (Array.isArray(item.images) && item.images.length > 0) {
                photoCount = item.images.length;
            }
            // Check if there's a photos array
            else if (Array.isArray(item.photos) && item.photos.length > 0) {
                photoCount = item.photos.length;
            }
            // Estimate based on business metrics (DataForSEO doesn't provide photo counts reliably)
            else {
                const reviewCount = item.rating?.votes_count || 0;
                const ratingValue = item.rating?.value || 0;
                const isClaimed = item.is_claimed || false;
                
                // Smart estimation algorithm based on business characteristics
                let basePhotos = 25; // Default baseline
                
                if (reviewCount > 0) {
                    // More reviews = more photos (but with diminishing returns)
                    // Formula: Base + (reviews / divisor) with caps
                    if (reviewCount < 10) {
                        basePhotos = 15 + (reviewCount * 2); // 15-35 photos
                    } else if (reviewCount < 50) {
                        basePhotos = 35 + Math.floor(reviewCount / 2); // 35-60 photos
                    } else if (reviewCount < 200) {
                        basePhotos = 60 + Math.floor(reviewCount / 3); // 60-126 photos
                    } else {
                        basePhotos = 126 + Math.floor(reviewCount / 5); // 126-226+ photos
                    }
                    
                    // Adjust for rating (higher rated places tend to have more photos)
                    if (ratingValue >= 4.5) {
                        basePhotos = Math.floor(basePhotos * 1.3);
                    } else if (ratingValue >= 4.0) {
                        basePhotos = Math.floor(basePhotos * 1.15);
                    } else if (ratingValue < 3.0 && ratingValue > 0) {
                        basePhotos = Math.floor(basePhotos * 0.7);
                    }
                    
                    // Claimed businesses typically have more photos
                    if (isClaimed) {
                        basePhotos = Math.floor(basePhotos * 1.2);
                    }
                } else {
                    // No reviews: estimate based on claimed status
                    basePhotos = isClaimed ? 30 : 12;
                }
                
                // Cap between realistic bounds
                photoCount = Math.min(350, Math.max(8, basePhotos));
                
                if (index === 0) {
                    logger.log('🔍 [DataForSEO] Photo estimation:', { 
                        reviewCount, 
                        ratingValue,
                        isClaimed,
                        estimated: photoCount 
                    });
                }
            }

            return {
                placeId: item.place_id || item.cid, // DataForSEO might use cid
                name: item.title,
                address: item.address,
                location: {
                    latitude: item.latitude,
                    longitude: item.longitude
                },
                rating: item.rating?.value || 0,
                reviewsCount: item.rating?.votes_count || 0,
                photoCount: photoCount, // Add photo count
                priceLevel: item.price_level,
                businessStatus: item.is_claimed ? 'OPERATIONAL' : 'UNKNOWN', // Approximation
                types: item.category ? [item.category] : [],
                phoneNumber: item.phone,
                website: item.url, // This might be Google Maps URL, check for website_url
                websiteUri: item.website, // Actual website
                openingHours: item.work_hours ? item.work_hours.timetable : [],
                photos: item.main_image ? [{ photo_reference: item.main_image }] : [],
                distance: item.distance // If available
            };
        });
    }
}

export default new DataForSEOService();
